import {
    UserPlusIcon,
    UserMinusIcon,
    ClipboardDocumentIcon,
    ChatBubbleLeftEllipsisIcon,
    ChatBubbleLeftRightIcon,
    DocumentCheckIcon,
    ExclamationCircleIcon,
    XMarkIcon,
    CheckCircleIcon,
    MinusCircleIcon,
    ArchiveBoxIcon,
    DocumentMinusIcon,
    CheckBadgeIcon,
    ClipboardDocumentListIcon,
    InboxIcon,
    CheckIcon,
    TagIcon,
    ExclamationTriangleIcon,
    ArrowUpTrayIcon,
    DocumentPlusIcon,
    TrashIcon,
} from '@heroicons/react/24/solid';
import { NotificationType } from '../../types/notification';
import { ReactComponent as WorkpapersLogo } from '../../assets/workpapers-fill.svg';

type NotificationIconTypeProps = {
    type: NotificationType;
};

export function NotificationIconType({ type }: NotificationIconTypeProps): JSX.Element {
    switch (type) {
        case NotificationType.WORKPAPER_ASSIGNED_TO:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-indigo-100">
                    <WorkpapersLogo className="h-4 w-4 " />
                </span>
            );

        case NotificationType.WORKPAPER_ASSIGNEES_ADDED:
        case NotificationType.WORKPAPER_GUESTS_ADDED:
        case NotificationType.WORKSHEET_ASSIGNEES_ADDED:
        case NotificationType.OTHER_SCHEDULE_ASSIGNEES_ADDED:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-green-100">
                    <UserPlusIcon className="w-4 h-4 text-green-900" />
                </span>
            );
        case NotificationType.WORKSHEET_ASSIGNED_TO:
        case NotificationType.OTHER_SCHEDULE_ASSIGNED_TO:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-yellow-100">
                    <ClipboardDocumentListIcon className="w-4 h-4 text-yellow-900" />
                </span>
            );
        case NotificationType.WORKPAPER_UNASSIGNED_FROM:
        case NotificationType.WORKPAPER_ASSIGNEES_REMOVED:
        case NotificationType.WORKSHEET_UNASSIGNED_FROM:
        case NotificationType.WORKPAPER_GUESTS_REMOVED:
        case NotificationType.WORKSHEET_ASSIGNEES_REMOVED:
        case NotificationType.OTHER_SCHEDULE_UNASSIGNED_FROM:
        case NotificationType.OTHER_SCHEDULE_ASSIGNEES_REMOVED:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-red-100">
                    <UserMinusIcon className="w-4 h-4 text-red-900" />
                </span>
            );

        case NotificationType.WORKPAPER_ACTIVE:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-indigo-100">
                    <ClipboardDocumentIcon className="w-4 h-4 text-indigo-900" />
                </span>
            );

        case NotificationType.WORKPAPER_ARCHIVED:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-gray-100">
                    <ArchiveBoxIcon className="w-4 h-4 text-gray-900" />
                </span>
            );

        case NotificationType.WORKPAPER_COMPLETED:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-green-100">
                    <CheckBadgeIcon className="w-4 h-4 text-green-900" />
                </span>
            );

        case NotificationType.WORKPAPER_EXPORT_COMPLETED:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-green-100">
                    <ArrowUpTrayIcon className="w-4 h-4 text-green-900" />
                </span>
            );

        case NotificationType.WORKPAPER_EXPORT_ERROR:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-red-100">
                    <ExclamationTriangleIcon className="w-4 h-4 text-red-900" />
                </span>
            );

        case NotificationType.WORKSHEET_CREATED:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-green-100">
                    <DocumentPlusIcon className="h-4 w-4 text-green-900" />
                </span>
            );

        case NotificationType.WORKSHEET_UNPREPARED:
            return <MinusCircleIcon className="w-7 h-7 text-red-600" />;

        case NotificationType.WORKSHEET_EXCLUDED:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-gray-100">
                    <XMarkIcon className="w-4 h-4 text-gray-900" />
                </span>
            );

        case NotificationType.WORKSHEET_AWAITING_REVIEW:
            return <ExclamationCircleIcon className="w-7 h-7 text-blue-500" />;

        case NotificationType.WORKSHEET_REVIEWED:
            return <CheckCircleIcon className="w-7 h-7 text-green-600" />;

        case NotificationType.WORKSHEET_DELETED:
            return <TrashIcon className="w-7 h-7 text-red-600" />;

        case NotificationType.OTHER_SCHEDULE_ARCHIVED:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-gray-100">
                    <ArchiveBoxIcon className="w-4 h-4 text-gray-900" />
                </span>
            );

        case NotificationType.OTHER_SCHEDULE_AWAITING_REVIEW:
            return <ExclamationCircleIcon className="w-7 h-7 text-yellow-500" />;

        case NotificationType.OTHER_SCHEDULE_REVIEWED:
            return <CheckCircleIcon className="w-7 h-7 text-green-600" />;

        case NotificationType.COMMENT_REPLIED:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-purple-100">
                    <ChatBubbleLeftEllipsisIcon className="w-4 h-4 text-purple-900" />
                </span>
            );

        case NotificationType.COMMENT_ACTIVE:
        case NotificationType.COMMENT_RESOLVED:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-pink-100">
                    <ChatBubbleLeftRightIcon className="w-4 h-4 text-pink-900" />
                </span>
            );

        case NotificationType.COMMENT_TAGGED:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-purple-100">
                    <TagIcon className="w-4 h-4 text-purple-900" />
                </span>
            );

        case NotificationType.QUERY_REPLIED:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-blue-100">
                    <ChatBubbleLeftEllipsisIcon className="w-4 h-4 text-blue-900" />
                </span>
            );

        case NotificationType.QUERY_ACTIVE:
        case NotificationType.QUERY_RESOLVED:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-red-100">
                    <InboxIcon className="w-4 h-4 text-red-900" />
                </span>
            );

        case NotificationType.QUERY_TAGGED:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-blue-100">
                    <TagIcon className="w-4 h-4 text-blue-900" />
                </span>
            );

        case NotificationType.CERTIFICATION_PREPARER_DONE:
        case NotificationType.CERTIFICATION_REVIEWER_DONE:
        case NotificationType.CERTIFICATION_APPROVER_DONE:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-green-100">
                    <CheckIcon className="w-4 h-4 text-green-900" />
                </span>
            );

        case NotificationType.CERTIFICATION_PREPARER_READY:
        case NotificationType.CERTIFICATION_REVIEWER_READY:
        case NotificationType.CERTIFICATION_APPROVER_READY:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-yellow-100">
                    <DocumentCheckIcon className="w-4 h-4 text-yellow-900" />
                </span>
            );

        case NotificationType.CERTIFICATION_PREPARER_REMOVED:
        case NotificationType.CERTIFICATION_REVIEWER_REMOVED:
        case NotificationType.CERTIFICATION_APPROVER_REMOVED:
        case NotificationType.CERTIFICATION_PREPARER_STATUS_CHANGE:
        case NotificationType.CERTIFICATION_REVIEWER_STATUS_CHANGE:
        case NotificationType.CERTIFICATION_APPROVER_STATUS_CHANGE:
            return (
                <span className="h-7 w-7 rounded-full flex items-center justify-center bg-pink-100">
                    <DocumentMinusIcon className="w-4 h-4 text-pink-900" />
                </span>
            );

        default:
            return <></>;
    }
}
